import React, { useState, useEffect, useMemo } from 'react';
import MatchListing from './MatchListing';
import { TabGroup } from '../common';
import { type Match, type MatchCounts, MATCH_STATUS } from '../../types/match';
import { useNetworkMatches } from '../../hooks/useMatch';
import { useQueryClient } from '@tanstack/react-query';
import { MATCH_KEY } from '../../utils/query-keys';

interface ApprovedMatchesProps {
  onDataChange?: (count: number) => void;
  startDate?: Date | null;
  /** Multi-event: scope to one event; null = all events. */
  eventId?: string | null;
  endDate?: Date | null;
  search: string;
  counts: MatchCounts;
  /** Multi-event: event-aware empty-state copy from the parent. */
  emptyMessage?: string;
}

const ApprovedMatches = ({
  startDate,
  eventId,
  endDate,
  search,
  counts,
  emptyMessage,
}: ApprovedMatchesProps) => {
  // Apr-25: sub-tab labels rewritten for clarity. Old "All Approved
  // / Awaiting" felt recursive (an "Approved" sub-tab inside the "Approved"
  // parent tab). New labels distinguish based on what's actually different
  // between the buckets:
  //   • All — both states combined (kept as the default landing view)
  //   • Mutual — both you AND the other person tapped Approve (was "Approved")
  //   • Waiting on them — you approved, they haven't decided (was "Awaiting")
  // Consistent with the status-text rewrites in utils/match.tsx::getStatusText
  // shipped earlier today (e4e437a).
  // Jun-18 (a user V1 test): tooltipKey added so each sub-tab carries an ⓘ
  // explainer (TabGroup renders it). Copy lives in tooltips/copy.ts.
  const tabs = [
    {
      label: 'All',
      value: 'all',
      tooltipKey: 'matches-subtab-all' as const,
    },
    {
      label: 'Mutual',
      value: MATCH_STATUS.APPROVED,
      // Jun-19 (a user V1 test, live-verify): guard against undefined counts.
      // matchCounts refetches under a new query key when the date filter
      // changes, so `counts` (= matchCounts?.result) is briefly undefined and
      // `counts.approved.approved` threw, white-screening the whole Matches page.
      count: counts?.approved?.approved ?? 0,
      tooltipKey: 'matches-subtab-mutual' as const,
    },
    {
      label: 'Waiting on them',
      value: MATCH_STATUS.AWAITING_OTHER,
      count: counts?.approved?.awaiting_other ?? 0,
      tooltipKey: 'matches-subtab-waiting' as const,
    },
  ];
  const queryClient = useQueryClient();

  const [tab, setTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(21);
  const {
    data: matches,
    isLoading,
    refetch: fetchNetworkMatches,
  } = useNetworkMatches({
    page: currentPage,
    limit,
    status: MATCH_STATUS.APPROVED,
    sub_status: tab,
    start_date: startDate?.toISOString() ?? undefined,
    event_id: eventId ?? undefined,
    end_date: endDate?.toISOString() ?? undefined,
  });

  const filteredMatches = useMemo(() => {
    if (search.length > 2) {
      const searchLower = search.toLowerCase();
      return matches?.result.items.filter((match: Match) => {
        const name = match.other_user?.name ?? '';
        const objective = match.other_user?.objective ?? '';
        return (
          name.toLowerCase().includes(searchLower) ||
          objective.toLowerCase().includes(searchLower)
        );
      });
    }
    return matches?.result.items;
  }, [search, matches]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleTabChange = (tab: string) => {
    setTab(tab);
    queryClient.invalidateQueries({ queryKey: [...MATCH_KEY, 'matchCounts'] });
  };

  useEffect(() => {
    if (endDate && currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchNetworkMatches();
    }
  }, [endDate, currentPage, tab]);

  return (
    <>
      <TabGroup
        className='w-fit bg-white border-2 border-gray-200 whitespace-nowrap overflow-x-auto scrollbar-hide w-full md:w-fit '
        activeTabClassName='!bg-body-800 !border-body-800 hover:!border-body-800 !text-white hover:!shadow-none min-w-[8rem] whitespace-nowrap px-4'
        tabClassName='whitespace-nowrap px-4 min-w-[8rem] w-full'
        tabSize='sm'
        tabs={tabs}
        activeTab={tab}
        onChange={handleTabChange}
      />
      <MatchListing
        matches={filteredMatches}
        currentPage={currentPage}
        totalPages={matches?.result.meta.pages ?? 0}
        totalItems={matches?.result.meta.total ?? 0}
        limit={limit}
        onPageChange={handlePageChange}
        loading={isLoading}
        emptyMessage={emptyMessage}
      />
    </>
  );
};

export default ApprovedMatches;
